/**
 * Reducer helpers for dynamic block Lookup Table + Block Properties Table
 * commands (DYN-2 B.2).
 *
 *   - geometry/create-lookup-table
 *   - geometry/update-lookup-table
 *   - geometry/delete-lookup-table
 *   - geometry/set-block-properties-table
 *   - geometry/add-block-property
 *   - geometry/update-block-property
 *   - geometry/remove-block-property
 *   - geometry/reorder-block-properties
 *
 * All handlers enforce BEDIT gating (the active space must be
 * `block-edit` and its `blockEditName` must match the command's
 * `blockName`). Mutations live in command-engine; this module adapts
 * them to the EditorCommand union and the reducer pipeline.
 */

import {
  assembleDrawingDocument,
  blockPropertiesTableSchema,
  blockPropertyEntrySchema,
  lookupTableSchema,
  type BlockPropertiesTable,
  type BlockPropertyEntry,
  type DrawingDocument,
  type LookupTable,
} from "@dxf-ai-editor/drawing-model";
import {
  addBlockPropertyMutation,
  createLookupTableMutation,
  deleteLookupTableMutation,
  removeBlockPropertyMutation,
  reorderBlockPropertiesMutation,
  setBlockPropertiesTableMutation,
  updateBlockPropertyMutation,
  updateLookupTableMutation,
} from "@dxf-ai-editor/command-engine";
import type { EditorCommand } from "@dxf-ai-editor/contracts";

type CreateLookupTableCommand = Extract<
  EditorCommand,
  { type: "geometry/create-lookup-table" }
>;
type UpdateLookupTableCommand = Extract<
  EditorCommand,
  { type: "geometry/update-lookup-table" }
>;
type DeleteLookupTableCommand = Extract<
  EditorCommand,
  { type: "geometry/delete-lookup-table" }
>;
type SetBlockPropertiesTableCommand = Extract<
  EditorCommand,
  { type: "geometry/set-block-properties-table" }
>;
type AddBlockPropertyCommand = Extract<
  EditorCommand,
  { type: "geometry/add-block-property" }
>;
type UpdateBlockPropertyCommand = Extract<
  EditorCommand,
  { type: "geometry/update-block-property" }
>;
type RemoveBlockPropertyCommand = Extract<
  EditorCommand,
  { type: "geometry/remove-block-property" }
>;
type ReorderBlockPropertiesCommand = Extract<
  EditorCommand,
  { type: "geometry/reorder-block-properties" }
>;

function guardBeditContext(
  document: DrawingDocument,
  blockName: string,
): boolean {
  const blockEditSpace = document.spaces.find((s) => s.kind === "block-edit");
  if (!blockEditSpace || blockEditSpace.blockEditName !== blockName) {
    return false;
  }
  if (!document.blocks[blockName]) return false;
  return true;
}

function safeParseLookupTable(value: unknown): LookupTable | null {
  const result = lookupTableSchema.safeParse(value);
  return result.success ? result.data : null;
}

function safeParseBlockPropertiesTable(
  value: unknown,
): BlockPropertiesTable | null {
  const result = blockPropertiesTableSchema.safeParse(value);
  return result.success ? result.data : null;
}

function safeParseBlockPropertyEntry(
  value: unknown,
): BlockPropertyEntry | null {
  const result = blockPropertyEntrySchema.safeParse(value);
  return result.success ? result.data : null;
}

// ═══════════════════════════════════════════════════════════════════════
// Lookup table handlers
// ═══════════════════════════════════════════════════════════════════════

export function handleCreateLookupTable(
  document: DrawingDocument,
  command: CreateLookupTableCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  const table = safeParseLookupTable(command.table);
  if (!table) return document;
  try {
    const { updatedDocument } = createLookupTableMutation({
      document,
      blockName: command.blockName,
      table,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleUpdateLookupTable(
  document: DrawingDocument,
  command: UpdateLookupTableCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = updateLookupTableMutation({
      document,
      blockName: command.blockName,
      tableId: command.tableId,
      updates: command.updates as Partial<LookupTable>,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleDeleteLookupTable(
  document: DrawingDocument,
  command: DeleteLookupTableCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = deleteLookupTableMutation({
      document,
      blockName: command.blockName,
      tableId: command.tableId,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Block Properties Table handlers
// ═══════════════════════════════════════════════════════════════════════

export function handleSetBlockPropertiesTable(
  document: DrawingDocument,
  command: SetBlockPropertiesTableCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  const table = safeParseBlockPropertiesTable(command.table);
  if (!table) return document;
  try {
    const { updatedDocument } = setBlockPropertiesTableMutation({
      document,
      blockName: command.blockName,
      table,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleAddBlockProperty(
  document: DrawingDocument,
  command: AddBlockPropertyCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  const entry = safeParseBlockPropertyEntry(command.entry);
  if (!entry) return document;
  try {
    const { updatedDocument } = addBlockPropertyMutation({
      document,
      blockName: command.blockName,
      entry,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleUpdateBlockProperty(
  document: DrawingDocument,
  command: UpdateBlockPropertyCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = updateBlockPropertyMutation({
      document,
      blockName: command.blockName,
      key: command.key,
      updates: command.updates as Partial<BlockPropertyEntry>,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleRemoveBlockProperty(
  document: DrawingDocument,
  command: RemoveBlockPropertyCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = removeBlockPropertyMutation({
      document,
      blockName: command.blockName,
      key: command.key,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}

export function handleReorderBlockProperties(
  document: DrawingDocument,
  command: ReorderBlockPropertiesCommand,
): DrawingDocument {
  if (!guardBeditContext(document, command.blockName)) return document;
  try {
    const { updatedDocument } = reorderBlockPropertiesMutation({
      document,
      blockName: command.blockName,
      orderedKeys: command.orderedKeys,
    });
    return assembleDrawingDocument(updatedDocument);
  } catch {
    return document;
  }
}
